import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../contexts/ApiContext';
import { queryKeys } from '../lib/queryClient';

type AuctionStatus = 'PENDING' | 'ACTIVE' | 'COMPLETED';

const BIDS_PER_PAGE = 20;

export function useAuctions(status?: AuctionStatus | 'all') {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: queryKeys.auctions(status),
    queryFn: async () => {
      const statusParam =
        status === 'all' || !status
          ? (['PENDING', 'ACTIVE', 'COMPLETED'] as const)
          : (status as AuctionStatus);
      const response = await apiClient.api.auctionControllerGetActiveAuctions({
        status: statusParam as any,
      });
      return response.auctions || [];
    },
  });
}

export function useAuctionDetails(auctionId: string | undefined, enabled = true) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: queryKeys.auctionDetails(auctionId || ''),
    queryFn: () => apiClient.api.auctionControllerGetAuctionDetails(auctionId!),
    enabled: enabled && !!auctionId,
  });
}

export function useAuctionBidsInfinite(auctionId: string | undefined, enabled = true) {
  const apiClient = useApiClient();

  return useInfiniteQuery({
    queryKey: queryKeys.auctionBids(auctionId || ''),
    queryFn: async ({ pageParam = 1 }) => {
      const response = await apiClient.api.auctionControllerGetAuctionBids({
        auctionId: auctionId!,
        page: pageParam,
        limit: BIDS_PER_PAGE,
      });
      return {
        bids: response.bids || [],
        hasMore: response.hasMore ?? false,
        page: pageParam,
      };
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage.hasMore) return undefined;
      return lastPage.page + 1;
    },
    initialPageParam: 1,
    enabled: enabled && !!auctionId,
  });
}

export function usePlaceBid() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ auctionId, amount }: { auctionId: string; amount: number }) => {
      return apiClient.api.auctionControllerPlaceBid(auctionId, { amount });
    },
    onSuccess: (_, variables) => {
      // Invalidate auction details and bids
      queryClient.invalidateQueries({ queryKey: queryKeys.auctionDetails(variables.auctionId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.auctionBids(variables.auctionId) });
      // Also invalidate profile for updated balance
      queryClient.invalidateQueries({ queryKey: queryKeys.profile });
    },
  });
}
